/**
 * Copyright (c) 2020-2021 Perficient, Inc.
 * Unauthorized copying or distribution of this file prohibited without the express permission of Perficient.
 */

import React, { useState } from "react";
import { CardContent, List, Button, ListSubheader } from "@material-ui/core";
import { v4 as uuid } from "uuid";
import { ActionButtons } from "../../../../components/Common";
import { EmptyPlaceholder } from "../../../../components";
import { ConfirmDialog } from "../../../../components/ConfirmDialog/ConfirmDialog";
import { LanguageTermItem } from "./LanguageTermItem";

const validateTermRow = (termRow) => {
	if (!Array.isArray(termRow) || termRow.length > 10) {
		return false;
	}
	for (let idx = 0; idx < termRow.length; idx++) {
		const row = termRow[idx];
		if (!row.value || !row.languageCode) {
			return false;
		}
	}
	return true;
};

const checkTermExists = (editedTerm, translationsData, sourceLanguage) => {
	if (editedTerm && translationsData && sourceLanguage) {
		const { value: sourceTermVal, id: sourceTermId } =
			editedTerm.find((t) => t.languageCode === sourceLanguage?.languageCode) || {};
		for (let idx = 0; idx < translationsData.length; idx++) {
			const row = translationsData[idx];
			const { value: loopTermVal, id: loopTermId } =
				row.find((t) => t.languageCode === sourceLanguage?.languageCode) || {};
			if (loopTermVal === sourceTermVal && loopTermId !== sourceTermId) {
				return true;
			}
		}
	}
	return false;
};

export const LangTermDetails = ({
	supportedLanguages,
	isNewTerm,
	setFormEdited,
	formEdited,
	sourceLanguage,
	updateActiveTerm,
	activeTerm,
	onCancelChanges,
	translationsData,
	onSaveTranslations,
	onRemoveTranslations,
}) => {
	const [removeConfirmOpen, setRemoveConfirmOpen] = useState(false);

	const onTermUpdate = (id, key, value) => {
		const newTermRow = [...activeTerm];
		newTermRow.map((t) => {
			if (t.id === id) {
				t[key] = value;
			}
			return t;
		});
		updateActiveTerm(newTermRow);
	};

	const addTermTranslation = () => {
		const newTerm = [{ value: "", languageCode: "", id: uuid() }, ...activeTerm];
		updateActiveTerm(newTerm);
	};

	const removeTermTranslation = (translationId) => {
		const newTerm = activeTerm.filter((t) => t.id !== translationId);
		updateActiveTerm(newTerm);
	};

	const handleSave = () => {
		onSaveTranslations();
	};

	const isTermRowValid = activeTerm ? validateTermRow(activeTerm) : false;

	const isTermExists = activeTerm ? checkTermExists(activeTerm, translationsData, sourceLanguage) : false;

	const selectedLanguageCodes = activeTerm ? activeTerm.map((term) => term.languageCode) : [];

	const activeSupportLanguages = supportedLanguages.filter(
		(lang) => !selectedLanguageCodes.includes(lang.languageCode),
	);

	const sourceTerm = activeTerm?.find((term) => term.languageCode === sourceLanguage?.languageCode);
	const sourceTermLanguage = supportedLanguages?.find((lang) => lang.languageCode === sourceTerm?.languageCode);
	const disableAddTranslations = activeTerm?.length > 9; // Allowing to add translations upto 10.

	return (
		<CardContent>
			{!activeTerm ? (
				<EmptyPlaceholder message="To get started, select a term from the list on the left, or click the 'Add' button to create a new term." />
			) : (
				<>
					<div className="d-flex align-items-center justify-content-between flex-wrap">
						<h5 style={{ fontWeight: "bold", margin: 0 }}>{isNewTerm ? "Add Term" : "Edit Term"}</h5>
						<div className="d-flex">
							<ActionButtons
								showCancel
								showRemove={!isNewTerm}
								className="my-2 my-md-0"
								onCancel={onCancelChanges}
								onRemove={() => setRemoveConfirmOpen(true)}
								showSave={formEdited}
								onSave={handleSave}
								disableSave={!isTermRowValid || isTermExists}
							/>
						</div>
					</div>
					<div className="mt-4 d-flex align-items-center justify-content-between">
						<List
							className="w-100"
							subheader={
								<ListSubheader disableSticky component="div" className="p-0">
									{sourceTerm && (
										<div style={{ marginBottom: 8 }}>
											<LanguageTermItem
												key={sourceTerm.id}
												termValue={sourceTerm.value}
												isTermExists={isTermExists}
												id={sourceTerm.id}
												isSourceLang
												languageCode={sourceTerm.languageCode}
												setFormEdited={setFormEdited}
												supportedLanguages={[...activeSupportLanguages, sourceTermLanguage]}
												onRemove={() => {
													removeTermTranslation(sourceTerm?.id);
													setRemoveConfirmOpen(false);
												}}
												language={sourceTermLanguage}
												formEdited
												onTermUpdate={onTermUpdate}
											/>
										</div>
									)}
									<div
										style={{
											display: "flex",
											flexDirection: "row",
											justifyContent: "space-between",
											alignItems: "center",
										}}
									>
										<div>
											<span>Translations </span>
											<span className={disableAddTranslations ? "text-danger" : ""}>
												{" "}
												( {`${activeTerm.length - 1} / 9`} )
											</span>
										</div>
										<Button
											disabled={disableAddTranslations}
											variant="outlined"
											size="small"
											onClick={addTermTranslation}
										>
											Add Translation
										</Button>
									</div>
								</ListSubheader>
							}
						>
							{activeTerm.map((term) => {
								const language = supportedLanguages?.find((lang) => lang.languageCode === term.languageCode);
								const isSourceLang = language?.languageCode === sourceLanguage?.languageCode;
								if (isSourceLang) return "";
								return (
									<LanguageTermItem
										key={term.id}
										termValue={term.value}
										id={term.id}
										isSourceLang={isSourceLang}
										languageCode={term.languageCode}
										setFormEdited={setFormEdited}
										supportedLanguages={[...activeSupportLanguages, language]}
										onRemove={() => {
											removeTermTranslation(term?.id);
											setRemoveConfirmOpen(false);
										}}
										language={language}
										formEdited
										onTermUpdate={onTermUpdate}
									/>
								);
							})}
						</List>
					</div>
					<ConfirmDialog
						title={"Remove Term?"}
						message={"Are you sure you want to delete the term and all its translations?"}
						confirmOpen={removeConfirmOpen}
						onClose={async (confirmed) => {
							if (confirmed) {
								onRemoveTranslations();
							}
							setRemoveConfirmOpen(false);
						}}
					/>
				</>
			)}
		</CardContent>
	);
};
